import { useEffect, useMemo, useState } from 'react';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ComposedChart,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { type AdminMetricsResponse, type MetricsRange, adminApi } from '../../services/admin';
import { usePreferences } from '../../contexts/PreferencesContext';
import styles from './Admin.module.css';

const RANGE_LABELS: Record<MetricsRange, string> = {
    '7d': '7 dias',
    '30d': '30 dias',
    '90d': '90 dias',
    '12m': '12 meses',
};

const PALETTE = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#ec4899'];

const STATUS_COLORS: Record<string, string> = {
    ACTIVE: '#22c55e',
    TRIAL: '#6366f1',
    PAST_DUE: '#f59e0b',
    CANCELED: '#ef4444',
    SUSPENDED: '#64748b',
};

const STATUS_LABELS_PT: Record<string, string> = {
    ACTIVE: 'Ativa',
    TRIAL: 'Trial',
    PAST_DUE: 'Em atraso',
    CANCELED: 'Cancelada',
    SUSPENDED: 'Suspensa',
};
const statusPt = (s: string) => STATUS_LABELS_PT[s] ?? s;

const FEATURE_LABELS_PT: Record<string, string> = {
    lancamentos: 'Lançamentos',
    dashboard: 'Dashboard',
    cashflow: 'Fluxo de Caixa',
    reports: 'Relatórios',
    'chart-of-accounts': 'Plano de Contas',
    'plano-contas': 'Plano de Contas',
    filiais: 'Filiais',
    users: 'Usuários',
    subscriptions: 'Assinaturas',
    admin: 'Admin',
};
const featurePt = (f: string) => FEATURE_LABELS_PT[f] ?? f;

const CHANNEL_LABELS_PT: Record<string, string> = {
    IN_APP: 'In-App',
    EMAIL: 'E-mail',
    WHATSAPP: 'WhatsApp',
};
const DISPATCH_STATUS_LABELS_PT: Record<string, string> = {
    PENDING: 'Pendente',
    SENT: 'Enviada',
    FAILED: 'Falhou',
    SKIPPED: 'Ignorada',
};
const notifKeyPt = (key: string) => {
    const [channel, status] = key.split('_').length === 3
        ? [`${key.split('_')[0]}_${key.split('_')[1]}`, key.split('_')[2]]
        : key.split('_');
    return `${CHANNEL_LABELS_PT[channel] ?? channel} · ${DISPATCH_STATUS_LABELS_PT[status] ?? status}`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function tooltipLabel(fn: (iso: string) => string): any { return fn; }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function tooltipFmt(fn: (...args: any[]) => any): any { return fn; }

function fmtDate(iso: string, gran: string): string {
    const d = new Date(iso);
    if (gran === 'month') return d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function fmtCurrency(v: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v);
}

function fmtPct(v: number): string {
    return `${v.toFixed(1)}%`;
}

const CHART_HEIGHT = 220;

interface ChartCardProps {
    title: string;
    children: React.ReactNode;
}
function ChartCard({ title, children }: ChartCardProps) {
    return (
        <div className={styles.chartCard}>
            <p className={styles.chartTitle}>{title}</p>
            {children}
        </div>
    );
}

function Empty() {
    return <div className={styles.chartSkeleton}>Sem dados no período</div>;
}

interface Props {
    defaultRange?: MetricsRange;
}

export function AdminMetrics({ defaultRange = '30d' }: Props) {
    const { prefs } = usePreferences();
    const darkMode = prefs.darkMode;
    const [range, setRange] = useState<MetricsRange>(defaultRange);
    const [data, setData] = useState<AdminMetricsResponse | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        setData(null);
        adminApi.getMetrics(range)
            .then(setData)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [range]);

    // Chart 2 — merge new vs canceled subs
    const subsOverTime = useMemo(() => {
        if (!data) return [];
        return data.newSubscriptions.map((ns, i) => ({
            date: ns.date,
            novas: ns.count,
            canceladas: data.canceledSubscriptions[i]?.count ?? 0,
        }));
    }, [data]);

    // Chart 9 — pivot notifications by date → { date, channel_status: count }
    const notifsData = useMemo(() => {
        if (!data || data.notifications.length === 0) return { rows: [], keys: [] as string[] };
        const byDate = new Map<string, Record<string, number>>();
        const keysSet = new Set<string>();
        for (const n of data.notifications) {
            const key = `${n.channel}_${n.status}`;
            keysSet.add(key);
            if (!byDate.has(n.date)) byDate.set(n.date, {});
            byDate.get(n.date)![key] = n.count;
        }
        const rows = [...byDate.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, vals]) => ({ date, ...vals }));
        return { rows, keys: [...keysSet] };
    }, [data]);

    const gran = data?.granularity ?? 'day';
    const tickFormatter = (iso: string) => fmtDate(iso, gran);

    const skeleton = <div className={styles.chartSkeleton}>Carregando...</div>;

    // Cores dos eixos/grid/tooltip/legenda dos gráficos Recharts (não leem variáveis CSS automaticamente)
    const axisColor = darkMode ? '#94a3b8' : '#666';
    const gridColor = darkMode ? '#243d2d' : '#f1f5f9';
    const tooltipProps = darkMode
        ? {
            contentStyle: { backgroundColor: '#162a1e', border: '1px solid #2e4d3a', color: '#f1f5f9' },
            labelStyle: { color: '#f1f5f9' },
            itemStyle: { color: '#f1f5f9' },
        }
        : {};
    const legendStyle = { color: axisColor };

    return (
        <section className={styles.metricsSection}>
            <div className={styles.metricsHeader}>
                <h3>Métricas de Uso</h3>
                <div className={styles.rangeButtons}>
                    {(Object.keys(RANGE_LABELS) as MetricsRange[]).map(r => (
                        <button
                            key={r}
                            className={`${styles.rangeBtn} ${range === r ? styles.rangeBtnActive : ''}`}
                            onClick={() => setRange(r)}
                        >
                            {RANGE_LABELS[r]}
                        </button>
                    ))}
                </div>
            </div>

            <div className={styles.chartsGrid}>
                {/* 1 — Novos usuários */}
                <ChartCard title="Novos Usuários">
                    {loading ? skeleton : data?.newUsers.every(d => d.count === 0) ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                            <LineChart data={data?.newUsers} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                                <XAxis dataKey="date" tickFormatter={tickFormatter} tick={{ fontSize: 11, fill: axisColor }} />
                                <YAxis tick={{ fontSize: 11, fill: axisColor }} allowDecimals={false} />
                                <Tooltip {...tooltipProps} labelFormatter={tooltipLabel(tickFormatter)} formatter={tooltipFmt((v: number) => [v, 'Usuários'])} />
                                <Line type="monotone" dataKey="count" stroke="#6366f1" strokeWidth={2} dot={false} name="Usuários" />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                {/* 2 — Novas vs Canceladas */}
                <ChartCard title="Assinaturas: Novas vs Canceladas">
                    {loading ? skeleton : subsOverTime.every(d => d.novas === 0 && d.canceladas === 0) ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                            <ComposedChart data={subsOverTime} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                                <XAxis dataKey="date" tickFormatter={tickFormatter} tick={{ fontSize: 11, fill: axisColor }} />
                                <YAxis tick={{ fontSize: 11, fill: axisColor }} allowDecimals={false} />
                                <Tooltip {...tooltipProps} labelFormatter={tooltipLabel(tickFormatter)} />
                                <Legend wrapperStyle={legendStyle} />
                                <Area type="monotone" dataKey="novas" fill="#6366f120" stroke="#6366f1" strokeWidth={2} name="Novas" />
                                <Line type="monotone" dataKey="canceladas" stroke="#ef4444" strokeWidth={2} dot={false} name="Canceladas" />
                            </ComposedChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                {/* 3 — MRR */}
                <ChartCard title="MRR Estimado">
                    {loading ? skeleton : data?.mrr.every(d => d.value === 0) ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                            <AreaChart data={data?.mrr} margin={{ top: 4, right: 16, left: 10, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                                <XAxis dataKey="date" tickFormatter={tickFormatter} tick={{ fontSize: 11, fill: axisColor }} />
                                <YAxis tickFormatter={v => fmtCurrency(v)} tick={{ fontSize: 10, fill: axisColor }} width={80} />
                                <Tooltip {...tooltipProps} labelFormatter={tooltipLabel(tickFormatter)} formatter={tooltipFmt((v: number) => [fmtCurrency(v), 'MRR'])} />
                                <Area type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2} fill="url(#mrrGrad)" name="MRR" />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                {/* 3b — ARR */}
                <ChartCard title="ARR Estimado">
                    {loading ? skeleton : data?.arr.every(d => d.value === 0) ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                            <AreaChart data={data?.arr} margin={{ top: 4, right: 16, left: 10, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="arrGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15} />
                                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                                <XAxis dataKey="date" tickFormatter={tickFormatter} tick={{ fontSize: 11, fill: axisColor }} />
                                <YAxis tickFormatter={v => fmtCurrency(v)} tick={{ fontSize: 10, fill: axisColor }} width={80} />
                                <Tooltip {...tooltipProps} labelFormatter={tooltipLabel(tickFormatter)} formatter={tooltipFmt((v: number) => [fmtCurrency(v), 'ARR'])} />
                                <Area type="monotone" dataKey="value" stroke="#22c55e" strokeWidth={2} fill="url(#arrGrad)" name="ARR" />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                {/* 4 — Churn rate */}
                <ChartCard title="Churn Rate (%)">
                    {loading ? skeleton : data?.churnRate.every(d => d.value === 0) ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                            <LineChart data={data?.churnRate} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                                <XAxis dataKey="date" tickFormatter={tickFormatter} tick={{ fontSize: 11, fill: axisColor }} />
                                <YAxis tick={{ fontSize: 11, fill: axisColor }} tickFormatter={v => `${v}%`} />
                                <Tooltip {...tooltipProps} labelFormatter={tooltipLabel(tickFormatter)} formatter={tooltipFmt((v: number) => [fmtPct(v), 'Churn'])} />
                                <Line type="monotone" dataKey="value" stroke="#ef4444" strokeWidth={2} dot={false} name="Churn" />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                {/* 5 — Assinaturas por status (donut) */}
                <ChartCard title="Assinaturas por Status">
                    {loading ? skeleton : !data?.subscriptionsByStatus.length ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                            <PieChart>
                                <Pie
                                    data={data?.subscriptionsByStatus.map(s => ({
                                        ...s,
                                        label: statusPt(s.status),
                                    }))}
                                    dataKey="count"
                                    nameKey="label"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={55}
                                    outerRadius={90}
                                    paddingAngle={2}
                                >
                                    {data?.subscriptionsByStatus.map(entry => (
                                        <Cell key={entry.status} fill={STATUS_COLORS[entry.status] ?? '#94a3b8'} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    {...tooltipProps}
                                    formatter={tooltipFmt((v: number, name: string, props: { payload?: { count: number; percent?: number } }) => {
                                        const pct = props?.payload?.percent != null
                                            ? ` (${(props.payload.percent * 100).toFixed(0)}%)`
                                            : '';
                                        return [`${v}${pct}`, name];
                                    })}
                                />
                                <Legend
                                    formatter={(value: string) => value}
                                    layout="horizontal"
                                    verticalAlign="bottom"
                                    align="center"
                                    wrapperStyle={{ fontSize: 12, paddingTop: 8, color: axisColor }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                {/* 6 — Usuários por plano */}
                <ChartCard title="Usuários por Plano">
                    {loading ? skeleton : !data?.usersByPlan.length ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                            <BarChart
                                data={data?.usersByPlan}
                                layout="vertical"
                                margin={{ top: 4, right: 24, left: 8, bottom: 0 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 11, fill: axisColor }} allowDecimals={false} />
                                <YAxis type="category" dataKey="planName" tick={{ fontSize: 11, fill: axisColor }} width={90} />
                                <Tooltip {...tooltipProps} formatter={tooltipFmt((v: number) => [v, 'Usuários'])} />
                                <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} name="Usuários" />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                {/* 7 — Top features */}
                <ChartCard title="Top Features Acessadas">
                    {loading ? skeleton : !data?.topFeatures.length ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                            <BarChart
                                data={data?.topFeatures.map(f => ({ ...f, label: featurePt(f.feature) }))}
                                layout="vertical"
                                margin={{ top: 4, right: 24, left: 8, bottom: 0 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 11, fill: axisColor }} allowDecimals={false} />
                                <YAxis type="category" dataKey="label" tick={{ fontSize: 11, fill: axisColor }} width={130} />
                                <Tooltip {...tooltipProps} formatter={tooltipFmt((v: number) => [v, 'Acessos'])} />
                                <Bar dataKey="count" radius={[0, 4, 4, 0]} name="Acessos">
                                    {data?.topFeatures.map((_, i) => (
                                        <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                {/* 8 — DAU / WAU / MAU */}
                <ChartCard title="Engajamento de Usuários">
                    {loading ? skeleton : (
                        <div className={styles.kpiRow}>
                            <div className={styles.kpiItem}>
                                <div className={styles.kpiBig}>{data?.activeUsers.dau ?? 0}</div>
                                <div className={styles.kpiLabel}>Ativos hoje</div>
                            </div>
                            <div className={styles.kpiItem}>
                                <div className={styles.kpiBig}>{data?.activeUsers.wau ?? 0}</div>
                                <div className={styles.kpiLabel}>Ativos nos últimos 7 dias</div>
                            </div>
                            <div className={styles.kpiItem}>
                                <div className={styles.kpiBig}>{data?.activeUsers.mau ?? 0}</div>
                                <div className={styles.kpiLabel}>Ativos nos últimos 30 dias</div>
                            </div>
                        </div>
                    )}
                </ChartCard>

                {/* 9 — Notificações por canal/status */}
                <ChartCard title="Notificações Enviadas">
                    {loading ? skeleton : notifsData.rows.length === 0 ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={notifsData.rows} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                                <XAxis dataKey="date" tickFormatter={tickFormatter} tick={{ fontSize: 11, fill: axisColor }} />
                                <YAxis tick={{ fontSize: 11, fill: axisColor }} allowDecimals={false} />
                                <Tooltip
                                    {...tooltipProps}
                                    labelFormatter={tooltipLabel(tickFormatter)}
                                    formatter={tooltipFmt((v: number, name: string) => [v, notifKeyPt(name)])}
                                    isAnimationActive={false}
                                    wrapperStyle={{ maxHeight: 200, overflowY: 'auto', fontSize: 11 }}
                                />
                                <Legend
                                    formatter={(value: string) => notifKeyPt(value)}
                                    wrapperStyle={{ fontSize: 11, paddingTop: 8, color: axisColor }}
                                    layout="horizontal"
                                    verticalAlign="bottom"
                                    align="center"
                                />
                                {notifsData.keys.map((k, i) => (
                                    <Bar key={k} dataKey={k} stackId="a" fill={PALETTE[i % PALETTE.length]} name={k} />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>
            </div>
        </section>
    );
}
